import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useFormContext } from '../Form';
import isEqual from 'lodash.isequal';
import Address from '../Address';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import FormHelperText from '@material-ui/core/FormHelperText';
import getFullName from '@afosto/utils/getFullName';
import AddressSelectionDrawer from '../AddressSelectionDrawer';
import translations from './translations';

const AddressFormSection = ({
  contact,
  setContact,
  organisation,
  setOrganisation,
  type,
  name,
  title,
  disableSelection,
  setBasedOnContact,
  setBasedOnOrganisation,
  showSetAsBillingAddress,
  showSetAsShippingAddress,
}) => {
  const intl = useIntl();
  const { errors, setValue, watch, register, unregister } = useFormContext();
  const [showSelectAddress, setShowSelectAddress] = useState(false);
  const addressValue = watch(name);
  const setValueRef = useRef(setValue);
  const addressValueRef = useRef(addressValue);
  const organisationRef = useRef(organisation);

  const availableAddressIds = [
    organisation?.addresses?.primary?.id,
    ...(organisation?.addresses?.secondary ?? []).map(address => address.id),
    contact?.[type]?.primary?.id,
    ...(contact?.[type]?.secondary ?? []).map(address => address.id),
  ].filter(addressId => addressId);
  const previousAddressIdsRef = useRef(availableAddressIds);

  useEffect(() => {
    setValueRef.current = setValue;
    addressValueRef.current = addressValue;
  });

  /**
   * Handle select address.
   * @param {object} selectedAddress
   */
  const handleSelectAddress = selectedAddress => {
    setValue(name, selectedAddress);
    setShowSelectAddress(false);
  };

  /**
   * Handler fired after creating a new contact address
   * @param {object} createdAddress
   * @param {object} updatedContact
   */
  const handleCreateContactAddress = (createdAddress, updatedContact) => {
    setContact({ ...(contact ?? {}), ...updatedContact });
    handleSelectAddress(createdAddress);
  };

  /**
   * Handler fired after creating a new organisation address
   * @param {object} createdAddress
   * @param {object} updatedOrganisation
   */
  const handleCreateOrganisationAddress = (createdAddress, updatedOrganisation) => {
    setOrganisation(updatedOrganisation);
    handleSelectAddress(createdAddress);
  };

  useEffect(() => {
    if (register) {
      register({ name });
    }

    return () => {
      unregister(name);
    };
  }, [name, register, unregister]);

  useEffect(() => {
    const { current: value } = addressValueRef;
    const { current: setFieldValue } = setValueRef;
    const { current: previousAddressIds } = previousAddressIdsRef;
    const { current: previousOrganisation } = organisationRef;
    const organisationRemoved = !!previousOrganisation && !organisation;
    const isPreviousValue = previousAddressIds.includes(value?.id);
    const organisationIsDifferent =
      !!organisation?.id && !isEqual(organisation?.id, previousOrganisation?.id);

    if ((!value || isPreviousValue || organisationIsDifferent) && setBasedOnOrganisation) {
      if (organisation?.[type]?.primary?.id) {
        setFieldValue(name, organisation[type].primary);
        return;
      }
    }

    if (
      (!value || isPreviousValue || (organisationRemoved && setBasedOnOrganisation)) &&
      setBasedOnContact
    ) {
      if (contact?.[type]?.primary?.id) {
        setFieldValue(name, contact[type].primary);
      } else {
        setFieldValue(name, null);
      }
    }
  }, [organisation, type, contact, setBasedOnContact, setBasedOnOrganisation, name]);

  useEffect(() => {
    previousAddressIdsRef.current = availableAddressIds;
    organisationRef.current = organisation;
  });

  return (
    <>
      <Card style={{ marginBottom: 8 }}>
        <CardHeader
          title={title}
          titleTypographyProps={{ variant: 'h6' }}
          action={
            <Button
              color="primary"
              variant="outlined"
              onClick={() => setShowSelectAddress(true)}
              disabled={disableSelection}
            >
              {intl.formatMessage(translations.page.addressFormSection.button.select.label)}
            </Button>
          }
        />
        <CardContent>
          {addressValue && (
            <Address
              company={addressValue.organisation}
              fullName={getFullName(addressValue)}
              addressLine1={addressValue.address_line_1}
              addressLine2={addressValue.address_line_2}
              postalCode={addressValue.postal_code}
              city={addressValue.locality}
              country={addressValue.country_code}
              administrativeArea={addressValue.administrative_area}
            />
          )}

          {!addressValue && !errors[name] && (
            <Typography variant="body2">
              {intl.formatMessage(translations.page.addressFormSection.emptyMessage)}.
            </Typography>
          )}

          {errors[name] && <FormHelperText error>{errors[name].message}</FormHelperText>}
        </CardContent>
      </Card>
      <AddressSelectionDrawer
        isOpen={showSelectAddress}
        onClose={() => setShowSelectAddress(false)}
        contact={contact}
        organisation={organisation}
        selectedAddress={addressValue}
        setSelectedAddress={handleSelectAddress}
        onCreateContactAddress={handleCreateContactAddress}
        onCreateOrganisationAddress={handleCreateOrganisationAddress}
        showSetAsBillingAddress={showSetAsBillingAddress}
        showSetAsShippingAddress={showSetAsShippingAddress}
        type={type}
      />
    </>
  );
};

AddressFormSection.propTypes = {
  contact: PropTypes.object,
  setContact: PropTypes.func,
  organisation: PropTypes.object,
  setOrganisation: PropTypes.func,
  type: PropTypes.string,
  name: PropTypes.string,
  title: PropTypes.string,
  disableSelection: PropTypes.bool,
  setBasedOnContact: PropTypes.bool,
  setBasedOnOrganisation: PropTypes.bool,
  showSetAsBillingAddress: PropTypes.bool,
  showSetAsShippingAddress: PropTypes.bool,
};

AddressFormSection.defaultProps = {
  contact: null,
  setContact: undefined,
  organisation: null,
  setOrganisation: undefined,
  type: 'billing',
  name: 'address',
  title: undefined,
  disableSelection: false,
  setBasedOnContact: true,
  setBasedOnOrganisation: true,
  showSetAsBillingAddress: true,
  showSetAsShippingAddress: true,
};

export default AddressFormSection;
